/**
 * AuthorOnboardingDialog — Phase A creator-centric refactor.
 *
 * 3-step onboarding flow:
 *   A. Choose mode — URL auto-extract OR manual paste
 *   B. Provide sources (URL + audience picker, OR manual textareas)
 *   C. Progress view (real-time via useBackendTask)
 *
 * MVP scope: submits to /api/agent/content-plan/author/onboard, subscribes to
 * backend_task via useBackendTask. Review drafts step (AC-046 full review
 * panel) deferred — after completion, user lands on Creator tab with drafts
 * already saved.
 *
 * Testids:
 *   author-onboarding-dialog
 *   author-onboarding-mode-url
 *   author-onboarding-mode-manual
 *   author-onboarding-url-input
 *   author-onboarding-about-textarea
 *   author-onboarding-posts-textarea
 *   author-onboarding-audience-picker
 *   author-onboarding-extract-btn
 *   author-onboarding-cancel-btn
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Stack, Box, TextField, Typography, Alert,
  RadioGroup, FormControlLabel, Radio, Select, MenuItem,
  LinearProgress, Button, Switch,
} from '@mui/material';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import { useBackendTask } from '@modules/content-agent/hooks/useBackendTask';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { useSystemPlatforms } from '../../hooks/useSystemPlatforms';

const apiClient = new ContentPlanApiClient();

const TESTIDS = {
  dialog: 'author-onboarding-dialog',
  modeUrl: 'author-onboarding-mode-url',
  modeManual: 'author-onboarding-mode-manual',
  urlInput: 'author-onboarding-url-input',
  aboutTextarea: 'author-onboarding-about-textarea',
  postsTextarea: 'author-onboarding-posts-textarea',
  audiencePicker: 'author-onboarding-audience-picker',
  extractBtn: 'author-onboarding-extract-btn',
  cancelBtn: 'author-onboarding-cancel-btn',
  progress: 'author-onboarding-progress',
} as const;

export interface AudienceOption {
  id: string;
  name: string;
  description?: string;
}

export interface AuthorOnboardingDialogProps {
  open: boolean;
  projectId: string;
  userId: string;
  /** Existing author for Redo flow (AC-046a/b/072a) — when present, dialog
   *  offers Redo option in Step A. */
  existingAuthorId?: string;
  /**
   * DEPRECATED — kept for back-compat. Audiences are now auto-loaded from
   * project via AudienceService inside the dialog. This prop is ignored.
   */
  audiences?: AudienceOption[];
  /** Default locale from project / user context (ru-RU, en-US, ...) */
  defaultLocale?: string;
  /** When true, create another self-author instead of refreshing the baseline author. */
  createNewSelfAuthor?: boolean;
  onClose: () => void;
  /** Called when onboarding task completes (status='completed') */
  onComplete?: (authorId: string | null) => void;
}

type Mode = 'url' | 'manual';

export function AuthorOnboardingDialog(props: AuthorOnboardingDialogProps) {
  const {
    open,
    projectId,
    userId,
    existingAuthorId,
    createNewSelfAuthor = false,
    defaultLocale = 'en-US',
    onClose,
    onComplete,
  } = props;
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const { platforms: systemPlatforms } = useSystemPlatforms({ activeOnly: true });
  const manualPlatformOptions = useMemo(
    () => systemPlatforms.map((platform) => ({
      value: platform.platformId,
      label: platform.displayName,
    })),
    [systemPlatforms],
  );

  const [step, setStep] = useState<'choose' | 'input' | 'progress'>('choose');
  const [mode, setMode] = useState<Mode>('url');
  const [profileUrl, setProfileUrl] = useState('');
  const [aboutText, setAboutText] = useState('');
  const [postsRaw, setPostsRaw] = useState('');
  const [manualPlatformId, setManualPlatformId] = useState('');
  const [locale, setLocale] = useState(defaultLocale);
  const [confirmRedo, setConfirmRedo] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // AI model selection — Gemini-only (only Gemini supports the structured-output schema this handler needs)
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  // maxPosts — number cap OR 'all' (unlimited, BD default cap kicks in)
  const [maxPostsNum, setMaxPostsNum] = useState<number>(10);
  const [allPosts, setAllPosts] = useState(false);

  // Auto-load project audiences silently — no user-facing picker.
  // Empty result is OK: backend/AI handles empty audience context gracefully.
  const [autoLoadedAudiences, setAutoLoadedAudiences] = useState<AudienceOption[]>([]);
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const service = new AudienceService(projectId);
        const audiences = await service.list();
        if (cancelled) return;
        setAutoLoadedAudiences(
          audiences.map((a: any) => ({
            id: a.id ?? '',
            name: a.name ?? a.displayName ?? '(unnamed)',
            description: a.description,
          })),
        );
      } catch {
        if (!cancelled) setAutoLoadedAudiences([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  // Reset wizard state whenever dialog reopens — closing + reopening should
  // start fresh, not resume a stuck progress/error state.
  useEffect(() => {
    if (open) {
      setStep('choose');
      setModel(projectDefaultModel.value);
      setManualPlatformId(manualPlatformOptions[0]?.value ?? '');
      setTaskId(null);
      setSubmitError(null);
      setConfirmRedo(false);
    }
  }, [open, projectDefaultModel.value, manualPlatformOptions]);

  /** Return from progress state to Step A — e.g. to switch to manual mode
   *  when Apify scrape failed. Clears task + error so user can retry fresh. */
  const handleBackToStart = () => {
    setStep('choose');
    setTaskId(null);
    setSubmitError(null);
  };

  // Real-time task subscription once dispatched
  const { task } = useBackendTask(taskId ? projectId : null, taskId ?? '');

  // Parse pasted posts — split by '---' or double newlines
  const parsedPosts = useMemo(
    () =>
      postsRaw
        .split(/\n?---+\n?|\n\n+/g)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [postsRaw],
  );

  const canSubmit = useMemo(() => {
    // Audiences are auto-loaded from project — no user gating required.
    // Empty audiences is OK: AI handles empty context gracefully.
    if (mode === 'url' && !profileUrl.trim()) return false;
    if (mode === 'manual') {
      if (!manualPlatformId) return false;
      if (!aboutText.trim()) return false;
      if (parsedPosts.length < 1) return false;
    }
    if (existingAuthorId && !confirmRedo) return false;
    return true;
  }, [
    mode,
    profileUrl,
    aboutText,
    parsedPosts.length,
    existingAuthorId,
    confirmRedo,
    manualPlatformId,
  ]);

  // Detect completion + notify parent
  React.useEffect(() => {
    if (task?.status === 'completed' && onComplete) {
      const result = (task as any).result as { authorId?: string } | undefined;
      onComplete(result?.authorId ?? null);
    }
  }, [task?.status, onComplete]);

  async function handleExtract() {
    setSubmitError(null);
    try {
      const effectiveMode = existingAuthorId
        ? mode === 'url'
          ? ('redo-url' as const)
          : ('redo-manual' as const)
        : mode;

      // Silently pass all project audiences — loaded on dialog open.
      // User doesn't pick; backend/AI receives full context automatically.
      const targetAudienceIds = autoLoadedAudiences.map((a) => a.id);
      const targetAudienceContext = autoLoadedAudiences.map((a) => ({
        id: a.id,
        name: a.name,
        description: a.description,
      }));

      const response = await apiClient.authorOnboard({
        projectId,
        userId,
        mode: effectiveMode,
        profileUrl: mode === 'url' ? profileUrl.trim() : undefined,
        manualInput:
          mode === 'manual'
            ? {
                platformId: manualPlatformId,
                aboutText: aboutText.trim(),
                postTexts: parsedPosts,
              }
            : undefined,
        targetAudienceIds,
        targetAudienceContext,
        locale,
        existingAuthorId,
        createNewSelfAuthor,
        // Model + maxPosts options (schema 1.3.1+ extends maxPosts to number | 'all').
        // Manual mode ignores maxPosts since no scrape happens, but the field is harmless.
        model,
        maxPosts: allPosts ? 'all' : maxPostsNum,
      });

      if (!response.success || !response.taskId) {
        setSubmitError(response.error ?? 'Failed to start onboarding');
        return;
      }
      setTaskId(response.taskId);
      setStep('progress');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <BaseDialog open={open} onClose={onClose} data-testid={TESTIDS.dialog} maxWidth="md" fullWidth>
      <DialogHeader
        title={existingAuthorId ? 'Redo author profile' : 'Setup author profile'}
        onClose={onClose}
      />
      <DialogContent>
        <Stack spacing={3} sx={{ py: 2 }}>
          {existingAuthorId && step === 'choose' && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Redo overwrites existing author.</strong> Current profile,
                platform, voice, post examples, and topic pillars will be replaced.
                Confirm below to continue.
              </Typography>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <input
                    type="checkbox"
                    checked={confirmRedo}
                    onChange={(e) => setConfirmRedo(e.target.checked)}
                  />
                }
                label="I understand — overwrite existing author"
              />
            </Alert>
          )}

          {step === 'choose' && (
            <>
              <Typography variant="subtitle1">Choose how to fill the profile</Typography>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
                <FormControlLabel
                  value="url"
                  control={<Radio data-testid={TESTIDS.modeUrl} />}
                  label="Extract from my social profile (LinkedIn, Twitter/X, etc.)"
                />
                <FormControlLabel
                  value="manual"
                  control={<Radio data-testid={TESTIDS.modeManual} />}
                  label="Fill manually — paste About text + recent posts"
                />
              </RadioGroup>
              <Box>
                <Button onClick={() => setStep('input')}>Continue →</Button>
              </Box>
            </>
          )}

          {step === 'input' && (
            <>
              {mode === 'url' && (
                <TextField
                  label="Profile URL"
                  placeholder="https://linkedin.com/in/yourhandle"
                  value={profileUrl}
                  onChange={(e) => setProfileUrl(e.target.value)}
                  fullWidth
                  inputProps={{ 'data-testid': TESTIDS.urlInput }}
                  helperText="We'll scrape your public profile + last 10 posts via Apify (~$0.15, 15–30 sec)"
                />
              )}
              {mode === 'manual' && (
                <>
                  <Select
                    value={manualPlatformId}
                    onChange={(e) => setManualPlatformId(e.target.value as string)}
                    label="Platform"
                  >
                    {manualPlatformOptions.map((platform) => (
                      <MenuItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    label="About / Bio text"
                    placeholder="Paste your bio / about section here..."
                    value={aboutText}
                    onChange={(e) => setAboutText(e.target.value)}
                    multiline minRows={3} maxRows={8}
                    fullWidth
                    inputProps={{ 'data-testid': TESTIDS.aboutTextarea }}
                  />
                  <TextField
                    label="Posts (3–10 posts, separate by blank line or ---)"
                    placeholder={'First post...\n---\nSecond post...\n---\nThird post...'}
                    value={postsRaw}
                    onChange={(e) => setPostsRaw(e.target.value)}
                    multiline minRows={6} maxRows={20}
                    fullWidth
                    inputProps={{ 'data-testid': TESTIDS.postsTextarea }}
                    helperText={`${parsedPosts.length} post(s) detected`}
                  />
                </>
              )}

              <TextField
                label="Content language (locale)"
                placeholder="en-US, ru-RU, ..."
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                sx={{ maxWidth: 240 }}
                helperText={
                  autoLoadedAudiences.length > 0
                    ? `Auto-loaded ${autoLoadedAudiences.length} audience(s) from project`
                    : 'No audiences in project yet — AI will use general context'
                }
              />

              {/* AI options — Gemini-only (only Gemini supports the
                  structured-output schema this handler needs) + maxPosts cap */}
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box>
                  <AvailableModelSelector
                    projectId={projectId}
                    value={model}
                    onChange={setModel}
                    providers={['google']}
                    label="AI model"
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Pro is slower but produces tighter output.
                  </Typography>
                </Box>
                {mode === 'url' && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        label="Max posts to scrape"
                        size="small"
                        type="number"
                        disabled={allPosts}
                        value={allPosts ? '' : maxPostsNum}
                        onChange={(e) => setMaxPostsNum(Number(e.target.value) || 0)}
                        inputProps={{ min: 1, max: 1000, 'data-testid': 'author-onboarding-max-posts' }}
                        sx={{ width: 180 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={allPosts}
                            onChange={(e) => setAllPosts(e.target.checked)}
                            data-testid="author-onboarding-all-posts"
                          />
                        }
                        label="All posts"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      ~$0.005 per deep-scraped post.
                    </Typography>
                  </Box>
                )}
              </Stack>

              {submitError && <Alert severity="error">{submitError}</Alert>}
            </>
          )}

          {step === 'progress' && (
            <Box data-testid={TESTIDS.progress}>
              <Typography variant="h6">
                {(task as any)?.status === 'error'
                  ? 'Extraction failed'
                  : 'Extracting your profile...'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {(task as any)?.status === 'error'
                  ? 'See the error below. You can try a different method (e.g. switch to manual paste).'
                  : 'This usually takes 30–60 seconds. You can close this dialog — progress will continue in the background, status visible from the Creator tab banner.'}
              </Typography>
              {(task as any)?.status !== 'error' && <LinearProgress />}
              {task && (
                <Stack spacing={0.5} sx={{ mt: 3 }}>
                  <Typography variant="caption">
                    Status: {(task as any).status ?? 'running'}
                  </Typography>
                  {(task as any).currentPhaseKey && (
                    <Typography variant="caption">
                      Current phase: {(task as any).currentPhaseKey}
                    </Typography>
                  )}
                  {(task as any).error && (
                    <Alert severity="error">{(task as any).error}</Alert>
                  )}
                </Stack>
              )}
              {(task as any)?.status === 'error' && (
                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={handleBackToStart}>
                    Try different method
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogFooter
        primaryAction={
          step === 'input'
            ? ({
                label:
                  mode === 'url' ? 'Extract and analyze' : 'Analyze pasted content',
                onClick: handleExtract,
                disabled: !canSubmit,
                'data-testid': TESTIDS.extractBtn,
              } as any)
            : step === 'progress'
              ? ({
                  label: 'Close (continues in background)',
                  onClick: onClose,
                  'data-testid': TESTIDS.cancelBtn,
                } as any)
              : undefined
        }
      />
    </BaseDialog>
  );
}
